import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Alert } from '@/components/common/Alert';
import { Badge } from '@/components/common/Badge';
import { invoiceService } from '@/services/invoice.service';
import { Invoice, InvoiceStatus, PaymentMethod } from '@/types/invoice.types';
import { formatPrice } from '@/utils/formatters';
import {
  DocumentTextIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';

export const ListeFactures = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const location = useLocation();

  // Recharger les factures quand on arrive sur la page ou qu'on revient dessus
  useEffect(() => {
    loadInvoices();
  }, [statusFilter, filterDateFrom, filterDateTo, location.pathname]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const filters: any = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      // Mode de paiement toujours 'card' - pas de filtre nécessaire
      if (searchTerm) filters.search = searchTerm;
      if (filterDateFrom) filters.date_from = filterDateFrom + ' 00:00:00';
      if (filterDateTo) filters.date_to = filterDateTo + ' 23:59:59';
      
      const data = await invoiceService.getAll(filters);
      setInvoices(data);
    } catch (error) {
      console.error('Erreur lors du chargement des factures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadInvoices();
  };

  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);

  const handleStatusChange = async (invoiceId: number, newStatus: InvoiceStatus) => {
    try {
      const updatedInvoice = await invoiceService.updateStatus(invoiceId, newStatus);
      if (updatedInvoice) {
        // Mettre à jour l'état local immédiatement
        setInvoices(prevInvoices => 
          prevInvoices.map(inv => inv.id === invoiceId ? updatedInvoice : inv)
        );
      }
      // Recharger depuis le serveur pour s'assurer d'avoir les données à jour
      await loadInvoices();
      setNotification({ type: 'success', message: t('adminInvoices.statusUpdateSuccess') });
    } catch (error: any) {
      console.error('Erreur lors du changement de statut:', error);
      const errorMessage = error?.response?.data?.error || error?.message || t('adminInvoices.statusChangeError');
      setNotification({ type: 'error', message: `Erreur: ${errorMessage}` });
      // Recharger les factures pour restaurer l'état précédent
      await loadInvoices();
    }
  };

  const handleDownloadPDF = async (invoiceId: number) => {
    try {
      const invoice = invoices.find(i => i.id === invoiceId);
      const blob = await invoiceService.generatePDF(invoiceId);
      
      // Vérifier que c'est bien un PDF
      if (!blob || blob.type !== 'application/pdf') {
        throw new Error('Le fichier téléchargé n\'est pas un PDF valide');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture-${invoice?.invoice_number || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setNotification({ type: 'success', message: t('adminInvoices.pdfSuccess') });
    } catch (error: any) {
      console.error('Erreur lors de la génération du PDF:', error);
      setNotification({ type: 'error', message: error?.message || t('adminInvoices.pdfError') });
    }
  };

  const handleDuplicate = async (invoiceId: number) => {
    try {
      const newInvoice = await invoiceService.duplicate(invoiceId);
      navigate(`/admin/factures/${newInvoice.id}`);
      setNotification({ type: 'success', message: t('adminInvoices.duplicateSuccess') });
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      setNotification({ type: 'error', message: t('adminInvoices.duplicateError') });
    }
  };

  const getStatusBadge = (status: InvoiceStatus) => {
    const statusMap: Record<InvoiceStatus, { text: string; variant: 'success' | 'error' | 'warning' | 'info' | 'neutral' }> = {
      draft: { text: t('adminInvoices.draft'), variant: 'neutral' },
      sent: { text: t('adminInvoices.sent'), variant: 'info' },
      paid: { text: t('adminInvoices.paid'), variant: 'success' },
      overdue: { text: t('adminInvoices.overdue'), variant: 'error' },
      cancelled: { text: t('adminInvoices.cancelled'), variant: 'error' },
    };
    const config = statusMap[status] || statusMap.draft;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const getPaymentMethodText = (method: PaymentMethod) => {
    // Seulement carte disponible
    return t('adminInvoices.card');
  };

  // Les filtres sont gérés côté serveur, pas besoin de filtrer côté client
  const filteredInvoices = invoices;

  // Pagination
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, filterDateFrom, filterDateTo, searchTerm]);

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    totalAmount: invoices.reduce((sum, i) => sum + i.total_amount, 0),
  };

  const columns: ResponsiveTableColumn<Invoice>[] = [
    {
      key: 'invoice_number',
      label: t('adminInvoices.invoiceNumber'),
      render: (invoice) => <div className="table-cell-name">{invoice.invoice_number}</div>,
    },
    {
      key: 'customer',
      label: t('adminInvoices.client'),
      render: (invoice) => (
        <div>
          <div className="table-cell-name">{invoice.customer_name}</div>
          <div className="text-xs text-body">{invoice.customer_email}</div>
          {invoice.order_id && (
            <div className="text-xs text-light mt-1">{t('adminInvoices.orderRef', { id: invoice.order_id })}</div>
          )}
        </div>
      ),
    },
    {
      key: 'invoice_date',
      label: t('adminInvoices.date'),
      render: (invoice) => new Date(invoice.invoice_date).toLocaleDateString('fr-FR'),
      mobileHidden: true,
    },
    {
      key: 'total_amount',
      label: t('adminInvoices.amountIncl'),
      render: (invoice) => <div className="table-cell-number">{formatPrice(invoice.total_amount)}</div>,
    },
    {
      key: 'status',
      label: t('adminInvoices.status'),
      render: (invoice) => getStatusBadge(invoice.status),
    },
    {
      key: 'payment_method',
      label: t('adminInvoices.payment'),
      render: (invoice) => getPaymentMethodText(invoice.payment_method),
      mobileHidden: true,
    },
    {
      key: 'actions',
      label: t('adminInvoices.actions'),
      render: (invoice) => (
        <div className="product-actions">
          <button
            className="action-btn action-view"
            onClick={() => navigate(`/admin/factures/${invoice.id}`)}
            title={t('adminInvoices.view')}
            aria-label={t('adminInvoices.viewAria')}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <button
            className="action-btn action-edit"
            onClick={() => navigate(`/admin/factures/${invoice.id}?edit=true`)}
            title={t('adminInvoices.edit')}
            aria-label={t('adminInvoices.editAria')}
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            className="action-btn action-edit"
            onClick={() => handleDownloadPDF(invoice.id)}
            title={t('adminInvoices.downloadPdf')}
            aria-label={t('adminInvoices.downloadAria')}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
          </button>
          <button
            className="action-btn action-edit"
            onClick={() => handleDuplicate(invoice.id)}
            title={t('adminInvoices.duplicate')}
            aria-label={t('adminInvoices.duplicateAria')}
          >
            <DocumentDuplicateIcon className="w-4 h-4" />
          </button>
        </div>
      ),
      mobileHidden: true,
    },
  ];

  return (
    <>

    {notification && (
              <Alert
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification(null)}
              />
            )}

            {/* KPI Cards */}
            <div className="kpi-row">
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <DocumentTextIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#3B82F6' }} />
                    <h3 className="kpi-title">{t('adminInvoices.totalInvoices')}</h3>
                  </div>
                  <span className="kpi-index">#1</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.total.toLocaleString('fr-FR')}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <DocumentDuplicateIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#6B7280' }} />
                    <h3 className="kpi-title">{t('adminInvoices.drafts')}</h3>
                  </div>
                  <span className="kpi-index">#2</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.draft.toLocaleString('fr-FR')}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <CheckCircleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#10B981' }} />
                    <h3 className="kpi-title">{t('adminInvoices.paidCount')}</h3>
                  </div>
                  <span className="kpi-index">#3</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.paid.toLocaleString('fr-FR')}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <CurrencyDollarIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#10B981' }} />
                    <h3 className="kpi-title">{t('adminInvoices.totalAmount')}</h3>
                  </div>
                  <span className="kpi-index">#4</span>
                </div>
                <div className="kpi-value">{loading ? '...' : formatPrice(stats.totalAmount)}</div>
              </div>
            </div>

            {/* Table des factures */}
            <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminInvoices.invoicesTitle')} ({filteredInvoices.length})</h3>
                <div className="flex gap-2 items-center">
                  {(searchTerm || statusFilter !== 'all' || filterDateFrom || filterDateTo) && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                        setFilterDateFrom('');
                        setFilterDateTo('');
                        loadInvoices();
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminInvoices.resetFilters')}
                    </button>
                  )}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate('/admin/factures/nouvelle')}
                  >
                    {t('adminInvoices.newInvoice')}
                  </button>
                </div>
              </div>

              {/* Filtres et recherche */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminInvoices.search')}</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('adminInvoices.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminInvoices.dateFrom')}</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminInvoices.dateTo')}</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">{t('adminInvoices.statusLabel')}</label>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'all')}
                  >
                    <option value="all">{t('adminInvoices.allStatuses')}</option>
                    <option value="draft">{t('adminInvoices.draft')}</option>
                    <option value="sent">{t('adminInvoices.sent')}</option>
                    <option value="paid">{t('adminInvoices.paid')}</option>
                    <option value="overdue">{t('adminInvoices.overdue')}</option>
                    <option value="cancelled">{t('adminInvoices.cancelled')}</option>
                  </select>
                </div>
                {/* Filtre de mode de paiement retiré - Seulement carte disponible */}
                {(filterDateFrom || filterDateTo) && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setFilterDateFrom('');
                      setFilterDateTo('');
                    }}
                  >
                    {t('adminInvoices.reset')}
                  </button>
                )}
              </div>

              <ResponsiveTable
                columns={columns}
                data={paginatedInvoices}
                loading={loading}
                loadingMessage={t('adminInvoices.loading')}
                emptyMessage={
                  searchTerm || statusFilter !== 'all' || filterDateFrom || filterDateTo
                    ? t('adminInvoices.noFilters')
                    : t('adminInvoices.empty')
                }
                mobileCardTitle={(invoice) => (
                  <div>
                    <div className="table-cell-name mb-1">{invoice.invoice_number}</div>
                    <div className="text-xs text-body">{invoice.customer_name}</div>
                  </div>
                )}
                mobileCardActions={(invoice) => (
                  <div className="product-actions" style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="action-btn action-view"
                      onClick={() => navigate(`/admin/factures/${invoice.id}`)}
                      title={t('adminInvoices.view')}
                      aria-label={t('adminInvoices.viewAria')}
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button
                      className="action-btn action-edit"
                      onClick={() => navigate(`/admin/factures/${invoice.id}?edit=true`)}
                      title={t('adminInvoices.edit')}
                      aria-label={t('adminInvoices.editAria')}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      className="action-btn action-edit"
                      onClick={() => handleDownloadPDF(invoice.id)}
                      title={t('adminInvoices.downloadPdf')}
                      aria-label={t('adminInvoices.downloadAria')}
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </button>
                    <button
                      className="action-btn action-edit"
                      onClick={() => handleDuplicate(invoice.id)}
                      title={t('adminInvoices.duplicate')}
                      aria-label={t('adminInvoices.duplicateAria')}
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
                tableClassName="products-table"
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '24px',
                  padding: '16px',
                  borderTop: '1px solid #e0e0e0'
                }}>
                  <div className="pagination-info" style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminInvoices.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredInvoices.length), total: filteredInvoices.length })}
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('adminInvoices.previous')}
                    </button>
                    <div className="pagination-pages" style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
    <>

                            <button
                              key={page}
                              className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                              onClick={() => setCurrentPage(page)}
                              style={{ minWidth: '36px' }}
                            >
                              {page}
                            </button>
                          
    </>
  );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '0 4px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('adminInvoices.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>
  
    </>
  );
};
